/**
 * Purpose: Configuration for game modes, age-group-dependent tier labels, and major options.
 * Owns: Mode display metadata, tier label mapping by age bracket, major list,
 *       age group display labels, and helper lookups.
 * Notes: Internal tier values (student/scholar/graduate) never change — only the
 *        user-facing LABELS change based on the player's age group (CS-21).
 *        Mode internal IDs map to the existing GameMode type in game.ts.
 */

import type { GameMode } from '../types/game';
import type { AgeGroup, Tier } from '../types/question';

// _______________________________________________
//              MODE DEFINITIONS
// _______________________________________________
// Each mode maps to an existing GameMode value and includes display metadata
// for the home screen mode cards and setup summary panels.

export interface ModeDefinition {
  /** Internal game mode ID (matches GameMode type) */
  id: GameMode;
  /** Display name shown on mode card */
  label: string;
  /** Short route slug for the setup URL */
  routeSlug: string;
  /** 1-2 sentence description for mode card */
  description: string;
  /** Summary points shown on the setup screen */
  summaryPoints: string[];
}

export const GAME_MODES: ModeDefinition[] = [
  {
    id: 'speedRun',
    label: 'Race',
    routeSlug: 'race',
    description: 'Roll the dice, race through classes, and be the first to finish!',
    summaryPoints: [
      'Dice movement determines seat advancement',
      'First player to finish all classes wins',
      'Speed is everything!',
    ],
  },
  {
    id: 'honorRoll',
    label: 'Honor Roll',
    routeSlug: 'honor-roll',
    description: 'Every class counts — the highest GPA takes the crown.',
    summaryPoints: [
      'GPA-based ranking determines the winner',
      'All classes must be completed',
      'Accuracy matters more than speed',
    ],
  },
  {
    id: 'pureClassroom',
    label: 'Classroom',
    routeSlug: 'classroom',
    description: 'No dice, no rush — just answer questions and earn your grades.',
    summaryPoints: [
      'Fixed number of questions per subject',
      'No dice rolling or seat movement',
      'GPA determines the winner',
    ],
  },
];

/** Lookup a mode definition by its GameMode ID. */
export function getModeById(id: GameMode): ModeDefinition {
  return GAME_MODES.find((m) => m.id === id) ?? GAME_MODES[0];
}

/** Lookup a mode definition by its route slug. */
export function getModeBySlug(slug: string): ModeDefinition | undefined {
  return GAME_MODES.find((m) => m.routeSlug === slug);
}

// _______________________________________________
//              AGE GROUP DISPLAY LABELS
// _______________________________________________
// Human-readable labels for each age group (CS-21: config-driven).

export interface AgeGroupOption {
  value: AgeGroup;
  label: string;
}

export const AGE_GROUP_OPTIONS: AgeGroupOption[] = [
  { value: 'kids-3-5', label: 'Ages 3-5' },
  { value: 'kids-6-8', label: 'Ages 6-8' },
  { value: 'kids-9-12', label: 'Ages 9-12' },
  { value: 'teens-13-17', label: 'High School' },
  { value: 'adults-18+', label: 'College' },
  { value: 'parents', label: 'Parents' },
];

// _______________________________________________
//              TIER LABEL MAPPING
// _______________________________________________
// The internal tier values (student/scholar/graduate) are constant.
// Display labels change based on the player's age bracket.
// Three brackets: young (3-11), academic (12-College), parents.

type AgeBracket = 'young' | 'academic' | 'parents';

/** Map an AgeGroup to its display bracket for tier labels. */
export function getAgeBracket(ageGroup: AgeGroup): AgeBracket {
  switch (ageGroup) {
    case 'kids-3-5':
    case 'kids-6-8':
    case 'kids-9-12':
      return 'young';
    case 'teens-13-17':
    case 'adults-18+':
      return 'academic';
    case 'parents':
      return 'parents';
  }
}

/** Tier display labels indexed by bracket and internal tier value. */
const TIER_LABELS: Record<AgeBracket, Record<Tier, string>> = {
  young: {
    student: 'Smartie',
    scholar: 'Awesome',
    graduate: 'Super Star',
  },
  academic: {
    student: 'Student',
    scholar: 'Scholar',
    graduate: 'Graduate',
  },
  parents: {
    student: 'Street Smart',
    scholar: 'Book Smart',
    graduate: 'Real Smart',
  },
};

/** Get the display label for a tier given a player's age group. */
export function getTierLabel(tier: Tier, ageGroup: AgeGroup): string {
  const bracket = getAgeBracket(ageGroup);
  return TIER_LABELS[bracket][tier];
}

/** Get all three tier options with display labels for an age group. */
export function getTierOptionsForAgeGroup(
  ageGroup: AgeGroup,
): { value: Tier; label: string }[] {
  const bracket = getAgeBracket(ageGroup);
  const labels = TIER_LABELS[bracket];
  return [
    { value: 'student', label: labels.student },
    { value: 'scholar', label: labels.scholar },
    { value: 'graduate', label: labels.graduate },
  ];
}

// _______________________________________________
//              MAJOR OPTIONS
// _______________________________________________
// Majors are only available for High School and College age groups.
// Younger groups and Parents default to 'General Ed'.

/** Whether the given age group requires a major selection. */
export function requiresMajor(ageGroup: AgeGroup): boolean {
  return ageGroup === 'teens-13-17' || ageGroup === 'adults-18+';
}

/** Available major choices (General Ed is always first). */
export const MAJOR_OPTIONS: string[] = [
  'General Ed',
  'Math',
  'Science',
  'History',
  'English',
  'Art',
  'Geography',
  'Music',
  'Sports',
];

/** Default major for age groups that don't select one. */
export const DEFAULT_MAJOR = 'General Ed';

// _______________________________________________
//              QUESTION AGE GROUP MAPPING
// _______________________________________________
// The 'parents' age group has no dedicated questions in the pack.
// Map it to 'adults-18+' for question selection.

/** Resolve an age group to the one used for question lookup. */
export function resolveQuestionAgeGroup(ageGroup: AgeGroup): AgeGroup {
  return ageGroup === 'parents' ? 'adults-18+' : ageGroup;
}
